import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, enableIndexedDbPersistence } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import { getAnalytics, isSupported, Analytics } from 'firebase/analytics';
import { logger } from '../utils/logger';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

logger.info('Firebase', 'Initializing Firebase', {
  projectId: firebaseConfig.projectId,
  authDomain: firebaseConfig.authDomain,
});

export const app = initializeApp(firebaseConfig);
logger.info('Firebase', 'Firebase app initialized');

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Initialize Analytics conditionally (may not be supported in all environments)
let analyticsInstance: Analytics | null = null;

isSupported().then(supported => {
  if (supported) {
    analyticsInstance = getAnalytics(app);
  }
});

export { analyticsInstance as analytics };

// Connect to emulators in development.
//
// This MUST run before enableIndexedDbPersistence() below: that call starts
// Firestore, and connectFirestoreEmulator() then throws "Firestore has already
// been started and its settings can no longer be changed" — an uncaught error
// at module load, so the app never mounts. Wrong order made the whole
// VITE_USE_EMULATORS=true path unusable.
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
  connectFirestoreEmulator(db, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Enable offline persistence for Firestore
enableIndexedDbPersistence(db).catch((err) => {
  if (err.code === 'failed-precondition') {
    console.warn('Firestore persistence failed: multiple tabs open');
  } else if (err.code === 'unimplemented') {
    console.warn('Firestore persistence not supported in this browser');
  }
});

// Also export as default for backwards compatibility
export default app;
